import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, ArrowLeft, Star, Coins, Award } from 'lucide-react';
import { useLitLegionTask } from '../hooks/useLitLegionTask';
import { TaskStatusBadge } from '../components/TaskStatusBadge';

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { task, loading, error, updateStatus } = useLitLegionTask(taskId);
  const [userInput, setUserInput] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const handleStatusUpdate = async (newStatus: string) => {
    setUpdating(true);
    setUpdateError(null);
    try {
      await updateStatus(newStatus, userInput || undefined);
      setUserInput('');
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update task');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/a/arche_lit_legion')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              {error ? 'Error' : 'Not Found'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || `Task not found: ${taskId}`}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { task: taskData, taskStatus, npc, coinRewards, badgeRewards } = task;
  const currentStatus = taskStatus?.status;

  const getNpcText = (): string | null => {
    if (!taskData) return null;
    switch (currentStatus) {
      case 'accepted':
        return taskData.npcTextInProgress || taskData.npcTextObjectives || null;
      case 'completed':
      case 'awarded':
        return taskData.npcTextSuccess || null;
      default:
        return taskData.npcTextIntro || null;
    }
  };

  const npcText = getNpcText();

  const getActionButton = () => {
    switch (currentStatus) {
      case undefined:
      case 'available':
        return (
          <Button onClick={() => handleStatusUpdate('accepted')} disabled={updating}>
            {updating ? 'Accepting...' : 'Accept Quest'}
          </Button>
        );
      case 'accepted':
        return (
          <div className="space-y-3">
            <Textarea
              placeholder="Enter your response or submission (optional)..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              rows={3}
            />
            <Button onClick={() => handleStatusUpdate('completed')} disabled={updating}>
              {updating ? 'Submitting...' : 'Complete Quest'}
            </Button>
          </div>
        );
      case 'completed':
        return (
          <p className="text-sm text-muted-foreground">
            Quest completed! Awaiting reward distribution.
          </p>
        );
      case 'awarded':
        return <p className="text-sm text-green-600 font-medium">Rewards have been distributed.</p>;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate('/a/arche_lit_legion')}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>

      <div>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-semibold">{taskData.name}</h1>
          <TaskStatusBadge status={currentStatus} />
        </div>
        <div className="flex items-center gap-2">
          {taskData.category && <Badge variant="outline">{taskData.category}</Badge>}
          {taskData.taskType && <Badge variant="secondary">{taskData.taskType}</Badge>}
        </div>
      </div>

      {taskData.description && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {taskData.description}
            </p>
          </CardContent>
        </Card>
      )}

      {npc && npcText && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">{npc.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm italic text-muted-foreground whitespace-pre-wrap">
              &ldquo;{npcText}&rdquo;
            </p>
          </CardContent>
        </Card>
      )}

      {taskData.npcTextObjectives &&
        currentStatus !== 'completed' &&
        currentStatus !== 'awarded' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Objectives</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {taskData.npcTextObjectives}
              </p>
            </CardContent>
          </Card>
        )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Rewards</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {taskData.xpReward > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <Star className="h-4 w-4 text-yellow-500" />
              <span>{taskData.xpReward} XP</span>
            </div>
          )}
          {coinRewards.length > 0 && (
            <div className="space-y-1">
              {coinRewards.map((coin) => (
                <div key={coin.coinRewardId} className="flex items-center gap-2 text-sm">
                  <Coins className="h-4 w-4 text-amber-500" />
                  <span>
                    {coin.quantity} {coin.coinName}
                  </span>
                </div>
              ))}
            </div>
          )}
          {badgeRewards.length > 0 && (
            <div className="space-y-1">
              {badgeRewards.map((badge) => (
                <div key={badge.badgeRewardId} className="flex items-center gap-2 text-sm">
                  <Award className="h-4 w-4 text-purple-500" />
                  <span>{badge.badgeId}</span>
                </div>
              ))}
            </div>
          )}
          {taskData.xpReward === 0 && coinRewards.length === 0 && badgeRewards.length === 0 && (
            <p className="text-sm text-muted-foreground">No rewards specified</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Action</CardTitle>
        </CardHeader>
        <CardContent>
          {updateError && <p className="text-sm text-destructive mb-3">{updateError}</p>}
          {getActionButton()}
        </CardContent>
      </Card>
    </div>
  );
}
